import { useSession } from "@src/appV2/Auth/api/useSession";
import { useLocation } from "react-router-dom";

import { instrumentNavigationItem } from "../../Shared/Navigation/instrumentNavigationItem";
import { useGetNavigationMenu } from "../../Shared/Navigation/navigationMenu/useGetNavigationMenu";
import {
  NavigationItem as NavigationItemParams,
  NavigationSection as NavigationSectionParams,
} from "../../Shared/Navigation/navigationMenu/types";
import { useAdminMenuPrivilegesProviderContext } from "../../Shared/Navigation/privilegesProviders/AdminMenuPrivilegesProvider";
import { useWorkerMenuPrivilegesProviderContext } from "../../Shared/Navigation/privilegesProviders/WorkerMenuPrivilegesProvider";
import { useWorkplaceMenuPrivilegesProviderContext } from "../../Shared/Navigation/privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { usePendingExtraWorkedTimeRequestCount } from "../../Shared/Navigation/usePendingExtraWorkedTimeRequestCount";
import { usePendingVisitOccurrenceCount } from "../../Shared/Navigation/usePendingVisitOccurrenceCount";
import { useUnreadChannelCount } from "../../Shared/Navigation/useUnreadChannelCount";
import { NavigationMenuItemMobile } from "@src/appV2/redesign/AppLayout/Mobile/Navigation/MenuItem";
import { NavigationMenuSectionTitleMobile } from "@src/appV2/redesign/AppLayout/Mobile/Navigation/MenuSectionTitle";

export interface NavigationMenuItemsMobileProps {
  isAdmin: boolean;
  isWorkplace: boolean;
  isWorker: boolean;
  closeSidenav: () => void;
}

export function NavigationMenuItemsMobile(props: NavigationMenuItemsMobileProps) {
  const { isAdmin, isWorker, isWorkplace, closeSidenav } = props;

  const currentPath = location.pathname;
  const adminMenuPrivileges = useAdminMenuPrivilegesProviderContext();
  const workplaceMenuPrivileges = useWorkplaceMenuPrivilegesProviderContext();
  const workerMenuPrivileges = useWorkerMenuPrivilegesProviderContext();
  const unreadChannelCount = useUnreadChannelCount();
  const pendingExtraWorkedTimeRequestCount = usePendingExtraWorkedTimeRequestCount();
  const pendingVisitOccurrenceCount = usePendingVisitOccurrenceCount();
  const session = useSession();
  const previousLocation = useLocation();

  const navigationMenu = useGetNavigationMenu(
    isAdmin,
    adminMenuPrivileges,
    isWorker,
    workerMenuPrivileges,
    isWorkplace,
    workplaceMenuPrivileges,
    currentPath,
    {
      unreadChannelCount,
      pendingExtraWorkedTimeRequestCount,
      pendingVisitOccurrenceCount,
      isMobile: true,
    }
  );

  function NavigationItem(params: NavigationItemParams) {
    const { label, iconType, isSelected, routeTo, onPreload, badgeCount } = params;
    const instrumented = instrumentNavigationItem(params, { isAdmin, session, previousLocation });

    return (
      <NavigationMenuItemMobile
        iconType={iconType}
        isSelected={isSelected}
        routeTo={routeTo}
        onPreload={onPreload}
        onClick={() => {
          instrumented.onClick?.();
          closeSidenav();
        }}
        badgeCount={badgeCount}
      >
        {label}
      </NavigationMenuItemMobile>
    );
  }

  function NavigationSection(params: NavigationSectionParams) {
    const { label, items } = params;

    return (
      <>
        <NavigationMenuSectionTitleMobile>{label}</NavigationMenuSectionTitleMobile>
        {items.map((item) => (
          <NavigationItem key={item.label} {...item} />
        ))}
      </>
    );
  }

  return (
    <>
      {navigationMenu.items.map((item) =>
        "items" in item ? (
          <NavigationSection key={item.label} {...item} />
        ) : (
          <NavigationItem key={item.label} {...item} />
        )
      )}
    </>
  );
}
